import { Component, Input, OnInit, WritableSignal, signal } from '@angular/core';

import { filter, map, tap } from 'rxjs/operators';

import { ArrayHelper } from 'tiime-expert-utils';
import {
  FallbackTextDirective,
  TiimeIconModule,
  TiimeIconPropertyDirective,
  TiimeLargePropertyDirective,
  TiimePropertiesDirective,
  TiimePropertyDirective
} from 'tiime-material';

import { PersonApiService } from '@api-services/person-api.service';
import { CommentaryTypeEnum } from '@enums/commentary-type.enum';
import { UserType } from '@enums/user-type.enum';
import { ContractForm } from '@gi/forms/contract-form';
import { User } from '@models/user';
import { AddressPropertyComponent } from '@shared-components/address-property/address-property.component';
import { CompanyPermanentNotePropertiesComponent } from '@shared-components/company-permanent-note-properties/company-permanent-note-properties.component';
import { CompanyPropertiesComponent } from '@shared-components/company-properties/company-properties.component';
import { CreateUserDialogData } from '@shared-dialogs/create-user-dialog/create-user-dialog-data';
import { CreateUserDialogService } from '@shared-dialogs/create-user-dialog/create-user-dialog.service';
import { CommentariesGroupByTypePipe } from '@shared-pipes/commentaries-group-by-type/commentaries-group-by-type.pipe';
import { PhonePipe } from '@shared-pipes/phone/phone.pipe';
import { UserSelectComponent } from '@shared-selectors/user-select/user-select.component';

@Component({
  selector: 'app-contract-contact-container',
  standalone: true,
  imports: [
    TiimePropertyDirective,
    TiimeLargePropertyDirective,
    TiimeIconPropertyDirective,
    FallbackTextDirective,
    TiimeIconModule,
    UserSelectComponent,
    PhonePipe,
    AddressPropertyComponent,
    CompanyPropertiesComponent,
    CompanyPermanentNotePropertiesComponent,
    CommentariesGroupByTypePipe
  ],
  templateUrl: './contract-contact-container.component.html',
  styleUrls: ['./contract-contact-container.component.scss'],
  hostDirectives: [TiimePropertiesDirective]
})
export class ContractContactContainerComponent implements OnInit {
  @Input() contractForm: ContractForm;

  readonly users: WritableSignal<User[]> = signal<User[]>([]);
  readonly companyPermanentNoteType = CommentaryTypeEnum.companyPermanentNote;
  readonly companyDisplayedProperties = ['name', 'legalForm', 'shareCapital'];

  constructor(
    private readonly personApiService: PersonApiService,
    private readonly createUserDialogService: CreateUserDialogService
  ) {}

  ngOnInit(): void {
    this.initUsers(this.contractForm.company.value.id);
  }

  openCreateUserDialog(): void {
    const createUserDialogData: CreateUserDialogData = {
      excludedAutocompleteUsers: this.users(),
      companyId: this.contractForm.company.value?.id
    };

    this.createUserDialogService
      .open({
        data: createUserDialogData
      })
      .afterClosed()
      .pipe(
        filter((createdUser: User) => !!createdUser),
        tap((createdUser: User) => {
          this.users.update(users => ArrayHelper.addOne(users, createdUser));
          this.contractForm.contact.setValue(createdUser);
        })
      )
      .subscribe();
  }

  private initUsers(companyId: number): void {
    this.personApiService
      .getPersons(null, null, null, companyId)
      .pipe(
        map((users: User[]) => users.filter((user: User) => user.type === UserType.user)),
        tap((users: User[]) => this.users.set(users))
      )
      .subscribe();
  }
}
